import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { Sparkles, Trophy, Users, Award } from "lucide-react";

const TIER_BADGE = {
  Seeker: "bg-cream-surface text-jjblue",
  Believer: "bg-gold/15 text-burgundy",
  Disciple: "bg-burgundy text-cream",
};

const PERIODS = [
  { key: "month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "all", label: "All time" },
];

// Public leaderboard. Hidden gracefully when nobody has any invite signups for the selected period.
export default function TopInvitersLeaderboard({ limit = 5, className = "", compact = false }) {
  const [period, setPeriod] = useState("month");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/inviters/top?limit=${limit}&period=${period}`)
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [limit, period]);

  // Always render at least the header so the toggle is reachable, but if every period is
  // empty on first all-time load we hide entirely to avoid clutter on a fresh community.
  if (loading) return null;
  if (period === "month" && rows.length === 0) {
    // Quietly hide on a brand new community.
    return null;
  }

  return (
    <section className={className} data-testid="top-inviters">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="overline text-burgundy flex items-center gap-2">
            <Trophy className="h-3.5 w-3.5" /> Inviter Hall of Honor
          </div>
          <h2 className={`font-serif text-jjblue mt-2 ${compact ? "text-2xl" : "text-3xl sm:text-4xl"}`}>
            Members carrying the family in.
          </h2>
          {!compact && (
            <p className="mt-2 text-[var(--jj-text-secondary)] max-w-xl">
              Each month our top inviters receive a gifted Believer-tier membership and a public honor. Want to join them? Share a watch-party invite.
            </p>
          )}
        </div>
        <div className="inline-flex rounded-full bg-cream-surface border border-jj p-1 text-xs" data-testid="top-inviters-period">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-full transition-colors ${
                period === p.key ? "bg-burgundy text-cream" : "text-jjblue hover:text-burgundy"
              }`}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="mt-6 jj-card p-6 text-center text-sm text-[var(--jj-muted)]">
          No attributed sign-ups for this window yet. Share an invite to be the first.
        </div>
      ) : (
        <ol className="mt-6 space-y-2">
          {rows.map((u, i) => {
            const tierClass = TIER_BADGE[u.tier] || TIER_BADGE.Seeker;
            const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}`;
            return (
              <li key={u.id} data-testid={`top-inviter-${u.id}`}>
                <Link
                  to={`/u/${u.id}`}
                  className="jj-card flex items-center gap-4 p-4 transition-colors hover:border-gold"
                >
                  <div className="w-10 text-center font-serif text-xl text-burgundy">{medal}</div>
                  <Link to={`/u/${u.id}`} className="block shrink-0">
                    <Avatar user={u} size={40} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 truncate">
                      <span className="font-medium text-jjblue truncate">{u.name || "A member"}</span>
                      {u.awarded_at && (
                        <span title="Honored previously" className="inline-flex items-center"><Award className="h-3.5 w-3.5 text-gold" /></span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-[var(--jj-muted)]">
                      <span className={`px-2 py-0.5 rounded-full ${tierClass}`}>{u.tier}</span>
                      <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {u.invite_signups} brought in</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-2xl text-gold leading-none">{u.invite_signups}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--jj-muted)] mt-1">signups</div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      )}

      {!compact && rows.length > 0 && (
        <div className="mt-5 text-xs text-[var(--jj-muted)] inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> Counts update the moment someone signs up through your link.
        </div>
      )}
    </section>
  );
}
